import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase/config";
import { mapStorageError } from "../utils/storageErrors";

// Servizio dichiarazioni dei redditi caricate direttamente dall'utente
// (sezione "Commercialista" della webapp). Storage: users/{uid}/declarations/...
// Firestore: users/{uid}/declarations (coperta dalla regola wildcard esistente
// su users/{userId}/{subcollection=**} in firestore.rules).

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function declarationsCollection(uid) {
  return collection(db, "users", uid, "declarations");
}

/**
 * Sottoscrive le dichiarazioni dell'utente, ordinate per anno decrescente.
 * @returns {Function} unsubscribe
 */
export function subscribeDeclarations(uid, callback) {
  if (!uid) return () => {};

  const q = query(declarationsCollection(uid), orderBy("year", "desc"));
  const unsubscribe = onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (err) => {
      console.error("Errore lettura dichiarazioni:", err);
      callback([]);
    }
  );

  return unsubscribe;
}

/**
 * Carica una dichiarazione PDF su Storage e crea il relativo documento Firestore.
 */
export async function uploadDeclaration(uid, { year, file }) {
  if (!uid) throw new Error("Utente non autenticato.");
  if (!year) throw new Error("Seleziona l'anno di riferimento.");
  if (!file) throw new Error("Seleziona un file da caricare.");
  if (file.type !== "application/pdf") {
    throw new Error("Formato non supportato. Carica un file PDF.");
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("File troppo grande. Massimo 10MB.");
  }

  const storagePath = `users/${uid}/declarations/${year}_${Date.now()}.pdf`;
  const storageRef = ref(storage, storagePath);
  let fileUrl;
  try {
    await uploadBytes(storageRef, file);
    fileUrl = await getDownloadURL(storageRef);
  } catch (err) {
    throw mapStorageError(err);
  }

  await addDoc(declarationsCollection(uid), {
    year: Number(year),
    fileName: file.name,
    fileUrl,
    storagePath,
    uploadedAt: serverTimestamp(),
    uploadedBy: "user",
  });
}

/**
 * Elimina il documento Firestore e il relativo file su Storage (se presente).
 */
export async function deleteDeclaration(uid, id, storagePath) {
  if (!uid || !id) throw new Error("Parametri mancanti per l'eliminazione.");

  await deleteDoc(doc(db, "users", uid, "declarations", id));

  if (storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (err) {
      // Ignora se il file non esiste più su Storage
      if (err.code !== "storage/object-not-found") {
        console.error("Errore eliminazione file dichiarazione da Storage:", err);
      }
    }
  }
}
